// Путь: D:\QR\QR-WEB\dashboard\src\components\User\MyReferrals.jsx
// Название: My Referrals Page v9.0 - DARY Universal Support
// Версия: 9.0.0
// Дата: 27.01.20 26

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyReferrals.css';

const API_URL = 'https://qr-money.onrender.com/api';
const DARY_CAMPAIGN_ID = 10;

const MyReferrals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [campaigns, setCampaigns] = useState([]);
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState({
    total_created: 0,
    activated: 0,
    total_earned: 0
  });
  
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [message, setMessage] = useState(null);
  
  const pollingRef = useRef(null);

  // ============================================
  // ЗАГРУЗКА ДАННЫХ
  // ============================================
  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { navigate('/login'); return; }

      const [campsRes, invRes] = await Promise.all([
        fetch(`${API_URL}/referrals/my-campaigns`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/referrals/my-invites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const campsData = await campsRes.json();
      const invData = await invRes.json();

      if (campsData.success) setCampaigns(campsData.data || []);
      if (invData.success) {
        setInvites(invData.data?.invites || []);
        setStats(invData.data?.stats || stats);
      }

    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // ============================================
  // POLLING для генерируемых картинок
  // ============================================
  useEffect(() => {
    const hasGenerating = invites.some(inv => inv.status === 'generating' || (!inv.image_url && inv.status === 'pending'));
    
    if (hasGenerating) {
      pollingRef.current = setInterval(() => {
        loadData();
      }, 5000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [invites, loadData]);

  // ============================================
  // ШАРИНГ
  // ============================================

  const downloadImage = async (url, code) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `DARY_Invite_${code}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      setMessage({ type: 'success', text: 'Картинка скачана!' });
      setTimeout(() => setMessage(null), 2000);
    } catch {
      window.open(url, '_blank');
    }
  };

  const shareToTelegram = async (inv) => {
    const isDary = inv.campaign_id === DARY_CAMPAIGN_ID || inv.status === 'archived_brand';
    const brandName = isDary ? 'DARY' : inv.campaign_name;
    const text = `🎁 Держи подарок от ${brandName}! Получи ${inv.bonus_new_user} DAR!`;
    const link = `https://dary.one/claim/${inv.short_code}`;
    
    if (navigator.share && navigator.canShare) {
      try {
        const response = await fetch(inv.image_url);
        const blob = await response.blob();
        const file = new File([blob], `DARY_Gift_${inv.short_code}.jpg`, { type: 'image/jpeg' });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            text: `${text}\n\n${link}`,
            files: [file]
          });
          return;
        }
      } catch (err) {
        console.log('Web Share with file failed:', err);
      }
    }
    
    await downloadImage(inv.image_url, inv.short_code);
    setMessage({ type: 'info', text: '📥 Картинка скачана! Отправь её в Telegram вручную' });
    setTimeout(() => {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`, '_blank');
      setMessage(null);
    }, 1500);
  };

  const shareToWhatsApp = async (inv) => {
    const isDary = inv.campaign_id === DARY_CAMPAIGN_ID || inv.status === 'archived_brand';
    const brandName = isDary ? 'DARY' : inv.campaign_name;
    const text = `🎁 Держи подарок от ${brandName}! Получи ${inv.bonus_new_user} DAR!`;
    const link = `https://dary.one/claim/${inv.short_code}`;
    
    if (navigator.share && navigator.canShare) {
      try {
        const response = await fetch(inv.image_url);
        const blob = await response.blob();
        const file = new File([blob], `DARY_Gift_${inv.short_code}.jpg`, { type: 'image/jpeg' });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            text: `${text}\n\n${link}`,
            files: [file]
          });
          return;
        }
      } catch (err) {
        console.log('Web Share with file failed:', err);
      }
    }
    
    await downloadImage(inv.image_url, inv.short_code);
    setMessage({ type: 'info', text: '📥 Картинка скачана! Отправь её в WhatsApp вручную' });
    setTimeout(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n\n${link}`)}`, '_blank');
      setMessage(null);
    }, 1500);
  };
  
  // ============================================
  // HELPERS
  // ============================================
  const getTimeLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Истекло';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}д ${hours}ч`;
    return `${hours}ч`;
  };

  const isGenerating = (inv) => {
    return inv.status === 'generating' || (!inv.image_url && inv.status === 'pending');
  };

  const isDaryUniversal = (inv) => {
    return inv.campaign_id === DARY_CAMPAIGN_ID || inv.status === 'archived_brand';
  };

  const canShare = (inv) => {
    // Можно шарить: ready, pending с картинкой, archived_brand с картинкой
    if (!inv.image_url || isGenerating(inv)) return false;
    return ['ready', 'pending', 'archived_brand'].includes(inv.status);
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return (
      <div className="ref-page">
        <div className="ref-loading">
          <div className="ref-spinner"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ref-page">
      {/* Message */}
      {message && (
        <div className={`ref-message ref-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Hero */}
      <div className="ref-hero">
        <div className="ref-hero-content">
          <h1>👥 Пригласи друзей</h1>
          <p>Отправь картинку другу — вы оба получите DAR!</p>
        </div>
        <div className="ref-hero-stats">
          <div className="ref-stat">
            <span className="ref-stat-icon">🎁</span>
            <span className="ref-stat-value">{stats.total_created}</span>
            <span className="ref-stat-label">Создано</span>
          </div>
          <div className="ref-stat ref-stat-success">
            <span className="ref-stat-icon">✅</span>
            <span className="ref-stat-value">{stats.activated}</span>
            <span className="ref-stat-label">Активировано</span>
          </div>
          <div className="ref-stat ref-stat-earn">
            <span className="ref-stat-icon">💰</span>
            <span className="ref-stat-value">{stats.total_earned}</span>
            <span className="ref-stat-label">Заработано</span>
          </div>
        </div>
      </div>

      {/* No Campaigns */}
      {campaigns.length === 0 && invites.length === 0 && (
        <div className="ref-empty">
          <span className="ref-empty-icon">📭</span>
          <h3>Нет доступных приглашений</h3>
          <p>Активируйте подарок от бренда — приглашения создадутся автоматически</p>
          <button className="ref-primary-btn" onClick={() => navigate('/vouchers/claim')}>
            🎁 Активировать подарок
          </button>
        </div>
      )}

      {/* Invites Grid */}
      {invites.length > 0 && (
        <div className="ref-section">
          <div className="ref-section-header">
            <h2>🎁 Мои приглашения</h2>
            <span className="ref-slots-info">
              {invites.filter(i => i.status !== 'activated').length} активных
            </span>
          </div>
          
          <div className="ref-grid">
            {invites.map(inv => (
              <div key={inv.id} className={`ref-card ref-card-${inv.status} ${isDaryUniversal(inv) ? 'ref-card-dary' : ''}`}>
                {/* Картинка */}
                <div className="ref-card-image">
                  {isGenerating(inv) ? (
                    <div className="ref-card-generating">
                      <div className="ref-generating-spinner"></div>
                      <span>Генерируем картинку...</span>
                    </div>
                  ) : inv.image_url ? (
                    <img src={inv.image_url} alt="Invite" />
                  ) : (
                    <div className="ref-card-placeholder">
                      <span>🎁</span>
                    </div>
                  )}
                  
                  {/* Статус бейдж */}
                  {!isGenerating(inv) && inv.status === 'archived_brand' && inv.image_url && (
                    <span className="ref-card-badge ref-badge-dary">🎁 DARY Universal</span>
                  )}
                  {!isGenerating(inv) && (inv.status === 'pending' || inv.status === 'ready') && inv.image_url && (
                    <span className="ref-card-timer">⏳ {getTimeLeft(inv.expires_at)}</span>
                  )}
                  {inv.status === 'activated' && (
                    <span className="ref-card-badge ref-badge-activated">✅ Активировано</span>
                  )}
                  {inv.status === 'expired' && (
                    <span className="ref-card-badge ref-badge-expired">❌ Истекло</span>
                  )}
                </div>

                {/* Инфо */}
                <div className="ref-card-info">
                  <h4>{isDaryUniversal(inv) ? 'DARY Universal' : inv.campaign_name}</h4>
                  {inv.status === 'archived_brand' && (
                    <span className="ref-dary-note">Кампания бренда завершена → DARY</span>
                  )}
                  <div className="ref-card-rewards">
                    <span className="ref-reward-friend">👤 Другу +{inv.bonus_new_user || 5} DAR</span>
                    <span className="ref-reward-you">🎉 Тебе +{inv.bonus_creator || 5} DAR</span>
                  </div>
                </div>

                {/* Кнопки действий */}
                {canShare(inv) && (
                  <div className="ref-card-actions">
                    <div className="ref-card-actions-row">
                      <button 
                        className="ref-btn ref-btn-download"
                        onClick={() => downloadImage(inv.image_url, inv.short_code)}
                      >
                        ⬇️ Скачать
                      </button>
                    </div>
                    <div className="ref-card-actions-row">
                      <button 
                        className="ref-btn ref-btn-telegram"
                        onClick={() => shareToTelegram(inv)}
                      >
                        📱 Telegram
                      </button>
                      <button 
                        className="ref-btn ref-btn-whatsapp"
                        onClick={() => shareToWhatsApp(inv)}
                      >
                        💬 WhatsApp
                      </button>
                    </div>
                  </div>
                )}

                {/* Для активированных - показываем кто активировал */}
                {inv.status === 'activated' && inv.activated_by_name && (
                  <div className="ref-card-activated-info">
                    <span>👤 {inv.activated_by_name}</span>
                    <span className="ref-earned-badge">+{inv.bonus_creator} DAR</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="ref-how-section">
        <button 
          className="ref-how-toggle"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
        >
          <span>❓ Как это работает</span>
          <span className={`ref-how-arrow ${showHowItWorks ? 'open' : ''}`}>▼</span>
        </button>
        
        {showHowItWorks && (
          <div className="ref-how-content">
            <div className="ref-how-steps">
              <div className="ref-how-step">
                <span className="ref-step-num">1</span>
                <div>
                  <strong>Активируй подарок</strong>
                  <p>Получи картинку от бренда и активируй её</p>
                </div>
              </div>
              <div className="ref-how-step">
                <span className="ref-step-num">2</span>
                <div>
                  <strong>Отправь другу</strong>
                  <p>3 приглашения создадутся автоматически — скачай и отправь</p>
                </div>
              </div>
              <div className="ref-how-step">
                <span className="ref-step-num">3</span>
                <div>
                  <strong>Получите бонусы</strong>
                  <p>Когда друг активирует — вы оба получаете DAR!</p>
                </div>
              </div>
            </div>
            <div className="ref-how-rules">
              <strong>📋 Правила:</strong> 3 приглашения на кампанию • DARY Universal — бессрочные • Нельзя активировать своё приглашение
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReferrals;